import { connect } from 'react-redux';
import { defineMessages, injectIntl } from 'react-intl';
import { stopSubmit, SubmissionError } from 'redux-form';
import { withRouter } from 'react-router-dom';
import _merge from 'lodash/merge';
import _get from 'lodash/get';
import _omit from 'lodash/omit';

import {
  contactActions as nucleusContactActions,
  contactModels,
  currentUserSelectors,
  featureSelectors,
  nucleusConstants
} from '@lifesize/nucleus';
import { FEATURE_FLAG_MULTIPLE_MODERATORS, FEATURE_FLAG_WAITING_ROOM } from 'constants/FeatureFlags';
import actions from 'actions/ContactModal';
import modalActions from 'actions/FullScreenModal';
import MeetingForm, { MEETING_FORM } from 'components/ContactModal/MeetingForm';
import {
  waitingRoomMeetingsSettingSelector,
  oneTimeMeetingFeatureSelector,
  requireMeetingPasscodeSelector,
  showPermanentMeetingsSelector
} from 'selectors/contact';
import { getContact, preLoadMissingMeetingInfo, selectContact } from 'utils/contact-utils';

// property = field name
// message key = error type from nucleus
const TOO_MANY_MEETINGS = 'TOO_MANY_MEETINGS';
const messages = {
  name: defineMessages({
    [nucleusConstants.VALIDATE_DUPLICATE]: {
      id: 'components.meetingForm.name.duplicateError',
      defaultMessage: '{value} already exists. Please choose a different name.'
    },
    [TOO_MANY_MEETINGS]: {
      id: 'components.meetingForm.name.maxMeetingError',
      defaultMessage: 'Maximum number of permanent meetings has been reached.'
    }
  }),
  generic: defineMessages({
    generic: {
      id: 'components.meetingForm.genericError',
      defaultMessage: 'Unable to create meeting.'
    }
  })
};

function mapDispatchToProps(dispatch, ownProps) {
  return {
    saveContact: (contactObj, isMultiModeratorEnabled) =>
      new Promise((resolve, reject) => {
        const contact = {
          ...new contactModels.Meeting(),
          ..._omit(contactObj, ['lecturer', 'moderator', 'moderatorID']),
          lecturerID: _get(contactObj, 'lecturer.id'),
          lecturerExtension: contactObj.uuid
            ? _get(contactObj, 'lecturer.dialString', null)
            : _get(contactObj, 'lecturer.dialString'),
          tempMeeting: false
        };
        if (isMultiModeratorEnabled) {
          // If multi-moderator supported
          contact.moderatorIDs = _get(contactObj, 'moderators', []).filter(m => !!m?.id).map((m) => m.id);
          contact.moderatorExtensions = _get(contactObj, 'moderators', []).filter(m => !!m?.id).map((m) => m.dialString);
        } else {
          contact.moderatorID = _get(contactObj, 'moderator.id');
          contact.moderatorExtension = _get(contactObj, 'moderator.dialString');
        }
        if (contact.meetingType === nucleusConstants.MEETING_TYPE_ONE_TIME) {
          contact.favorite = false;
        }
        preLoadMissingMeetingInfo(dispatch, contact)
          .then(() => dispatch(nucleusContactActions.saveContact(contact)))
          .then((newContact) => {
            selectContact(Object.assign({}, newContact, { isMeeting: true }), ownProps.history.push);
            dispatch(actions.unsetLecturer());
            dispatch(actions.unsetModerator());
            resolve(newContact);
          })
          .catch((error) => {
            const errorObj = {};
            if (typeof error === 'object' && Array.isArray(error)) {
              error.forEach((err) => {
                errorObj[err.context.key] =
                  messages[err.context.key] && messages[err.context.key][err.type]
                    ? ownProps.intl.formatMessage(messages[err.context.key][err.type], { value: err.context.value })
                    : err.type;
              });
              /* eslint-disable no-underscore-dangle */
              errorObj._error = 'Creating a contact failed';
              if (!errorObj.name) {
                errorObj.name = ownProps.intl.formatMessage(messages.generic.generic);
              }
              reject(new SubmissionError(errorObj));
            } else {
              // Another type of form error
              errorObj._error = error.errorDescription;
              if (error.errorCode === '409') {
                errorObj.name = ownProps.intl.formatMessage(messages.name[nucleusConstants.VALIDATE_DUPLICATE], {
                  value: contact.name
                });
              } else if (error.statusCode === 400 && error.errorDescription === 'DisplayNameExistError') {
                errorObj.name = ownProps.intl.formatMessage(messages.name[nucleusConstants.VALIDATE_DUPLICATE], {
                  value: contact.name
                });
              } else if (error.statusCode === 400 && error.errorDescription === 'MeetingLimitError') {
                errorObj.name = ownProps.intl.formatMessage(messages.name[TOO_MANY_MEETINGS]);
              } else {
                errorObj.name = ownProps.intl.formatMessage(messages.generic.generic);
              }
              dispatch(stopSubmit(MEETING_FORM));
              reject(new SubmissionError(errorObj));
              /* eslint-enable no-underscore-dangle */
            }
            reject();
          });
      }),
    handleCancel: () => {
      dispatch(modalActions.closeFullScreenModal());
    }
  };
}

const mapStateToProps = (state, ownProps) => {
  const isMultiModeratorEnabled = featureSelectors.getFeatureFlag(state)(FEATURE_FLAG_MULTIPLE_MODERATORS);
  const userId = _get(state, 'nucleus.userProfile.uuid');
  const selfResult = currentUserSelectors.myInfo(state);
  const meeting = Object.assign({}, ownProps.contactObj || _get(state, 'app.contactModal.contactObj') || {});
  let moderator = getContact(state, userId, meeting.moderatorID) || meeting.moderator;
  if (!moderator.id) {
    moderator = _get(state, 'app.contactModal.moderator');
  }

  let lecturer = getContact(state, userId, meeting.lecturerID);
  if (!lecturer.id) {
    lecturer = meeting.lecturer;
  } else {
    meeting.lecturer = lecturer;
  }

  const lecturerIsFocused = _get(state, 'app.contactModal.isLecturerFocused');
  const showPermanentMeeting = ownProps.isCreate && showPermanentMeetingsSelector(state);
  const oneTimeMeetingFeature = oneTimeMeetingFeatureSelector(state);
  const requireMeetingPasscode = requireMeetingPasscodeSelector(state);
  /*
      show OTM option when OTM and VMR both are true.
                      when VMR is not available
  */
  const showOneTimeMeeting =
    ownProps.isCreate && ((oneTimeMeetingFeature && showPermanentMeeting) || !showPermanentMeeting);
  const meetingTypeVmr = nucleusConstants.MEETING_TYPE_VMR;
  const meetingTypeOneTime = nucleusConstants.MEETING_TYPE_ONE_TIME;
  const selectedMeetingType = _get(state, ['app', 'form', MEETING_FORM, 'values', 'meetingType']);
  const savingContact = _get(state, 'app.contacts.savingContact');
  const showWaitingRoom = featureSelectors.getFeatureFlag(state)(FEATURE_FLAG_WAITING_ROOM);

  const waitingRoomDefault = waitingRoomMeetingsSettingSelector(state);
  return {
    user: { id: userId },
    lecturerIsFocused,
    moderator,
    lecturer,
    meeting,
    meetingTypeVmr,
    meetingTypeOneTime,
    showOneTimeMeeting,
    showPermanentMeeting,
    selectedMeetingType,
    savingContact,
    requireMeetingPasscode,
    enableReinitialize: true,
    keepDirtyOnReinitialize: true,
    isMultiModeratorEnabled,
    initialValues: _merge(
      {
        meetingType: showPermanentMeeting ? meetingTypeVmr : meetingTypeOneTime
      },
      {
        waitingRoom: meeting?.id ? !!meeting.waitingRoom : waitingRoomDefault
      },
      meeting,
      isMultiModeratorEnabled ? { moderators: meeting.moderators || [selfResult] } : {}
    ),
    showWaitingRoom
  };
};

export default withRouter(injectIntl(connect(mapStateToProps, mapDispatchToProps)(MeetingForm)));
